import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import activity from 'activity';
import rootStudentController from 'root-student-controller';
import _ from 'underscore';

import {
  StudentApplicationContext,
  StudentApplicationContextProvider,
} from '~shared/context/StudentApplicationContext';
import dots from 'Dots';
import platform from 'Platform';
import popup from 'PopupController';
import QuestionFeedbackView from 'QuestionFeedbackView';
import QuizResultsView from 'QuizResultsView';
import model from 'StudentQuizModel';
import StudentQuizView from 'StudentQuizView';
import utils from 'Utils';

import { ErrorView } from '../../../shared/error/ErrorView';
import { dynamicImport } from '../../../src/util/dynamicImport';

const insertEmbed = dynamicImport({
  moduleImport: () =>
    import(/* webpackChunkName:'insertEmbedded' */ '../../../src/embedded'),
  moduleProperty: (module) => module.insertEmbed,
});

function StudentQuizController() {}

StudentQuizController.prototype = _.extend(
  Object.create(Object.getPrototypeOf(rootStudentController)),
  {
    doQuiz: function () {
      if (!controller.shouldDoQuiz()) {
        controller.doNextRoute();
      } else {
        model.setVisibleQuestion();
        if (model.allowRepeatResponses()) {
          model.clearTextAnswers();
        }
        controller.renderView();
      }
    },

    renderView: function () {
      insertEmbed().then((insertEmbed) => {
        let viewProps = {
          controller: controller,
          isSpaceRace: activity.isSpaceRace(),
          spaceRaceClass: model.getSpaceRaceClass(),
          spaceRaceTeam: model.getSpaceRaceTeam(),
          quizProgress: model.getQuizProgress(),
          quizTimerSeconds:
            activity.has('settings') &&
            activity.get('settings').quiz_timer_seconds !== undefined &&
            activity.get('settings').quiz_timer_seconds !== -1
              ? activity.get('settings').quiz_timer_seconds
              : undefined,
          onTimerExpired: function () {
            if (!model.isStudentFinished() && !model.allQuestionsAnswered()) {
              activity.set('student_timer_expired', true);
            }
          },
          showSubmit: !model.isStudentNav(),
          isResponseValid: model.isResponseValid(),
          studentNav: model.isStudentNav() && !activity.isSpaceRace(),
          currentQuestion: model.getQuestionNumber(),
          totalQuestions: model.getQuestions().length,
          questionMedia: model.getVisibleQuestion()?.question_video,
        };

        let footer = document.getElementById('footer');

        if (model.isStudentNav()) {
          model.setAllQuestionStates();
          viewProps.navProps = model.getNavProps();
          if (platform.isPhone() && footer) {
            footer.style.visibility = 'hidden';
          }
        } else {
          if (platform.isPhone() && footer) {
            footer.style.visibility = 'visible';
          }
        }

        if (model.hasImage()) {
          viewProps.imageUrl = model.getImageUrl();
        }

        /**
         * TODO Future:
         *  1. Might need font-fallback for rendering of formulas in production.
         *     There's a slight flash of open brackets before rendering the actual latex formula.
         *
         *  2. Currently, this function gets called on every answer clicked and rebuilds the
         *     latex formula each time. Will need to optimize/memoize when we rebuild the student app.
         */

        const currentQuestion = model.getVisibleQuestion();
        const props = {
          ...viewProps,
          ...currentQuestion,
          question_text: insertEmbed
            ? insertEmbed(currentQuestion.question_text)
            : currentQuestion.question_text,
          answers: currentQuestion.answers.map((a) => ({
            ...a,
            text: insertEmbed ? insertEmbed(a.text) : a.text,
          })),
          showGradingWeight: model.showQuestionWeight(),
        };
        ReactDOM.render(
          <ErrorView>
            <StudentApplicationContextProvider
              applicationContext={StudentApplicationContext.Socrative}
            >
              <StudentQuizView {...props} />
            </StudentApplicationContextProvider>
          </ErrorView>,
          document.getElementById('main-content')
        );
      });
    },

    answerClicked: function (id) {
      model.toggleResponse(id);
      controller.renderView();
    },

    answerTextChanged: function (text) {
      model.setResponseText(text);
    },

    submitAnswerClicked: function () {
      if (model.isResponseValid()) {
        dots.start('submit-button');
        model.sendResponse(controller.responseSent);
      }
    },

    nextClicked: function () {
      model.setPendingNavIndex(model.getVisibleQuestion().order);
      controller.handleStudentNav();
    },

    previousClicked: function () {
      model.setPendingNavIndex(model.getVisibleQuestion().order - 2);
      controller.handleStudentNav();
    },

    questionNumberClicked: function (number) {
      model.setPendingNavIndex(number - 1);
      controller.handleStudentNav();
    },

    handleStudentNav: function () {
      model.preparePendingQuestion();
      if (model.isResponseChanged()) {
        model.sendResponse(controller.studentNavResponseSent);
      } else {
        controller.studentNavResponseSent(true);
      }
    },

    studentNavResponseSent: function (skipStorage) {
      if (!skipStorage) {
        model.storeSentResponse();
      }
      model.updateStudentNavIndex();
      controller.doQuiz();
    },

    responseSent: function () {
      dots.stop();
      if (
        !model.allowRepeatResponses() &&
        (model.isLastQuestion() || controller.shouldWaitForTeacherPaced())
      ) {
        if (model.shouldShowFeedback()) {
          controller.renderFeedback(
            model.isLastQuestion()
              ? controller.checkShowResults
              : controller.doNextRoute
          );
        } else {
          if (model.isLastQuestion()) {
            controller.checkShowResults();
          } else {
            controller.doNextRoute();
          }
        }
      } else {
        if (model.shouldShowFeedback()) {
          controller.renderFeedback(controller.doQuiz);
        } else {
          controller.doQuiz();
        }
      }
    },

    renderFeedback: function (callback) {
      insertEmbed().then((insertEmbed) => {
        ReactDOM.unmountComponentAtNode(
          document.getElementById('main-content')
        );

        let question = model.getVisibleQuestion();
        let response = question.student_response;

        let feedBackShim = document.createElement('div');
        feedBackShim.id = 'feedback-shim';
        document.body.appendChild(feedBackShim);

        ReactDOM.render(
          <QuestionFeedbackView
            isCorrect={response.get('is_correct')}
            questionText={
              insertEmbed
                ? insertEmbed(question.question_text)
                : question.question_text
            }
            gradingWeight={question.grading_weight}
            correctAnswerIds={response.get('correct_answer_ids')}
            answers={question.answers.map((a) => ({
              ...a,
              text: insertEmbed ? insertEmbed(a.text) : a.text,
            }))}
            textAnswers={
              question.type === QuestionTypes.FreeResponse
                ? response.get('correct_answers')
                : []
            }
            explanation={
              insertEmbed
                ? insertEmbed(response.get('explanation'))
                : response.get('explanation')
            }
            image={response.get('image')}
            questionMedia={question.question_image || question?.question_video}
            okClicked={function () {
              controller.closeModal('feedback-shim');
              callback();
            }}
            showGradingWeight={model.showQuestionWeight()}
            questionId={question.question_id}
          />,
          document.getElementById('feedback-shim'),
          function () {
            utils.blurPage();
          }
        );
      });
    },

    checkShowResults: function () {
      if (model.shouldShowResults()) {
        model.getResults(controller.renderResults);
      } else {
        controller.doNextRoute();
      }
    },

    buildLegacyQuestionStatsProps: function (data) {
      let { total, correct, neutral } = data;
      let divisor = total - neutral;

      return {
        score: `${correct}/${divisor}`,
        neutral: neutral,
        percent: divisor !== 0 ? correct / divisor : 0,
      };
    },

    buildQuestionStatsProps: function (data) {
      let { quiz_total, student_score, neutral } = data;

      return {
        score: `${student_score}/${quiz_total}`,
        neutral: neutral,
        percent: quiz_total !== 0 ? student_score / quiz_total : 0,
      };
    },

    renderResults: function (results) {
      ReactDOM.unmountComponentAtNode(document.getElementById('main-content'));
      let props =
        'quiz_total' in results && 'student_score' in results
          ? controller.buildQuestionStatsProps(results)
          : controller.buildLegacyQuestionStatsProps(results);

      props.percent = (props.percent * 100).toFixed(0) + '%';
      props['okClicked'] = () => controller.closeResults();

      let resultsShim = document.createElement('div');
      resultsShim.id = 'results-shim';
      document.body.appendChild(resultsShim);

      ReactDOM.render(<QuizResultsView {...props} />, resultsShim, function () {
        utils.blurPage();
      });
    },

    closeResults: function () {
      controller.closeModal('results-shim');
      controller.doNextRoute();
    },

    finishQuizClicked: function () {
      dots.start('student-finish-quiz');
      if (model.isResponseValid() && model.isResponseChanged()) {
        model.sendResponse(controller.getUnansweredQuestions);
      } else {
        controller.getUnansweredQuestions();
      }
    },

    getUnansweredQuestions: function () {
      model.getUnansweredQuestions(controller.confirmFinishQuiz);
    },

    confirmFinishQuiz: function (unanswered) {
      popup.render({
        title: translate('Please Confirm'),
        message:
          unanswered.length > 0
            ? model.getUnansweredMessage(unanswered)
            : translate('Are you sure you want to finish the quiz?'),
        buttonText:
          unanswered.length > 0 ? translate('Finish') : translate('Yes'),
        cancelText:
          unanswered.length > 0 ? translate('Cancel') : translate('No'),
        buttonClicked: function () {
          model.finishActivity(controller.activityFinished);
        },
        cancelClicked: function () {
          dots.stop();
          if (unanswered.length > 0) {
            model.initStudentNavIndex(); // Go to the highest unanswered question.
            controller.doQuiz();
          }
        },
      });
    },

    activityFinished: function () {
      dots.stop();
      controller.checkShowResults();
    },
  }
);

StudentQuizController.prototype.constructor = StudentQuizController;
let controller = new StudentQuizController();

module.exports = controller;
